<template>
    <div
        v-if="(creators && creators.length > 0) || (rejectedCreators && rejectedCreators.length > 0)"
        class="mt-4 rounded-lg border border-gray-200 bg-white cursor-pointer group transition-all duration-200 hover:shadow-lg hover:border-blue-300 hover:bg-blue-50/30"
        @click="$emit('view-full-results')"
    >
        <div class="p-6">
            <!-- Main Content Area -->
            <div class="flex items-center justify-between">
                <!-- Left Side: Avatars + Content -->
                <div class="flex items-center space-x-4 flex-1">
                    <!-- Creator Avatars Preview - Leading Position -->
                    <div v-if="creators && creators.length > 0" class="flex items-center space-x-3 relative isolate">
                        <div class="flex">
                            <div
                                v-for="(creator, index) in previewCreators"
                                :key="creator._id || index"
                                class="relative"
                                :style="{
                                    marginLeft: index > 0 ? '-25px' : '0',
                                    zIndex: 50 - index
                                }"
                            >
                                <!-- Avatar with border and hover effects -->
                                <div class="border-3 border-white ring-2 ring-gray-200 group-hover:ring-blue-300 transition-all duration-200 shadow-md rounded-full">
                                    <Avatar
                                        :size="48"
                                        :username="creator.name"
                                        :srcs="getAvatarSources(creator)"
                                        :rounded="true"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Fallback Icon for Rejected Creators -->
                    <div v-else-if="rejectedCreators && rejectedCreators.length > 0" class="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 group-hover:bg-red-200 transition-all duration-200">
                        <UsersIcon class="h-6 w-6 text-red-600" />
                    </div>

                    <!-- Text Content -->
                    <div class="flex-1 min-w-0">
                        <h4 class="font-semibold text-gray-900 group-hover:text-blue-900 transition-colors">
                            <span v-if="totalCreators && totalCreators > 0">
                                Found {{ totalCreators }} Creator{{ totalCreators !== 1 ? 's' : '' }}
                            </span>
                            <span v-else-if="rejectedCreators && rejectedCreators.length > 0">
                                {{ rejectedCreators.length }} Rejected Creator{{ rejectedCreators.length !== 1 ? 's' : '' }}
                            </span>
                        </h4>
                        <p class="text-sm text-gray-500 group-hover:text-blue-600 transition-colors mt-1">
                            <span v-if="creators && creators.length > 0">
                                {{ getCreatorSummary() }}
                            </span>
                            <span v-else-if="rejectedCreators && rejectedCreators.length > 0">
                                Click to see why creators were filtered out
                            </span>
                        </p>
                    </div>
                </div>

                <!-- Right Side: Arrow -->
                <div class="flex items-center ml-4">
                    <ChevronRightIcon class="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-all duration-200 group-hover:translate-x-1" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { MEDIA_CDN_BASE } from '@/utils/media'
import _ from 'lodash'
import { computed } from 'vue'
import {
    UsersIcon,
    ChevronRightIcon,
} from 'lucide-vue-next'
import Avatar from '@/components/Avatar.vue'

export default {
    name: 'CreatorResults',

    components: {
        UsersIcon,
        ChevronRightIcon,
        Avatar,
    },

    props: {
        totalCreators : {
            type: Number,
            default: 0
        },
        creators: {
            type: Array,
            default: () => []
        },
        rejectedCreators: {
            type: Array,
            default: () => []
        },
        maxPreview: {
            type: Number,
            default: 5
        },
        totalReach : {
            type: Number,
            default: null
        },
    },

    emits: ['view-full-results'],

    setup(props) {
        const previewCreators = computed(() => {
            return props.creators.slice(0, props.maxPreview)
        })

        const getCreatorSummary = () =>  props?.totalReach > 0 ? `${formatNumber(props.totalReach)} total reach` : 'Various platforms'

        const formatNumber = (num) => {
            if (num >= 1000000) {
                return `${(num / 1000000).toFixed(1)}M`
            }
            if (num >= 1000) {
                return `${(num / 1000).toFixed(1)}K`
            }
            return num?.toLocaleString() || 'N/A'
        }

        const getAvatarSources = (creator) => {
            return _.map(creator.source_ids, ({ id, platform_id }) => {
                return `${MEDIA_CDN_BASE}/${platform_id}/avatars/${
                    'large'
                }/${id}`
            })
        }

        return {
            previewCreators,
            getCreatorSummary,
            formatNumber,
            getAvatarSources,
        }
    },
}
</script>